import { t } from '@lingui/core/macro';

/**
 * Utility function for writing to the clipboard with proper error handling and feature detection
 */
export interface ClipboardWriteOptions {
  /** Custom error message to show on failure */
  errorMessage?: string;
  /** Whether to throw errors instead of returning them */
  throwOnError?: boolean;
}

export interface ClipboardWriteResult {
  success: boolean;
  error?: Error;
}

/**
 * Writes clipboard items to the system clipboard with feature detection and error handling
 * @param items - Array of ClipboardItem objects to write
 * @param options - Optional configuration for error handling
 * @returns Promise that resolves to success status and any error
 */
export const writeToClipboard = async (
  items: ClipboardItem[],
  options: ClipboardWriteOptions = {}
): Promise<ClipboardWriteResult> => {
  const { errorMessage, throwOnError = false } = options;

  try {
    // Feature detection - throws if not supported
    checkClipboardSupport();

    // Attempt to write to clipboard
    // Note: navigator.clipboard.write(items) may throw if permissions are not granted
    // or in unsupported browsers. Errors are handled in the catch block below.
    await navigator.clipboard.write(items);

    return { success: true };
  } catch (error) {
    const clipboardError = new Error(
      errorMessage || (error instanceof Error ? error.message : t`Failed to write to clipboard`)
    );

    if (throwOnError) throw clipboardError;
    return { success: false, error: clipboardError };
  }
};

/**
 * Writes text to the clipboard as a convenience method
 * @param text - Text to write to clipboard
 * @param options - Optional configuration for error handling
 * @returns Promise that resolves to success status and any error
 */
export const writeTextToClipboard = async (
  text: string,
  options: ClipboardWriteOptions = {}
): Promise<ClipboardWriteResult> => {
  try {
    // Feature detection - throws if not supported
    checkClipboardSupport();

    // Try to use writeText method first (more efficient)
    if (navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return { success: true };
    }

    // Fallback to generic write method
    const textItem = new window.ClipboardItem({
      'text/plain': new Blob([text], { type: 'text/plain' }),
    });

    return await writeToClipboard([textItem], options);
  } catch (error) {
    const clipboardError = new Error(
      options.errorMessage || (error instanceof Error ? error.message : t`Failed to write text to clipboard`)
    );

    if (options.throwOnError) throw clipboardError;
    return { success: false, error: clipboardError };
  }
};

/**
 * Checks if the clipboard API is available and functional
 * Executes all tests in series and throws on failure
 * @throws Error if clipboard is not supported
 */
export const checkClipboardSupport = (): void => {
  // Check if we're in a secure context (required for clipboard API)
  const secureContext =
    typeof window !== 'undefined' && typeof window.isSecureContext !== 'undefined' ? window.isSecureContext : false;

  if (!secureContext) {
    throw new Error('Clipboard API requires a secure context (HTTPS)');
  }

  // Check if clipboard API is available
  if (!navigator.clipboard) {
    throw new Error('Clipboard API not supported in this browser');
  }

  // Check if clipboard write method is available
  if (!navigator.clipboard.write || !navigator.clipboard.writeText) {
    throw new Error('Clipboard write method not supported in this browser');
  }

  if (typeof window.ClipboardItem !== 'function') {
    throw new Error('ClipboardItem is not supported in this browser');
  }

  // All checks passed
};
